import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import {
    IconBucketDroplet,
    IconColorPicker,
    IconDownload,
    IconReplace,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import LogoColorChangerPreview from './LogoColorChangerPreview';
import {
    ACCEPT,
    COLOR_PRESETS,
    MAX_FILE_BYTES,
    RECOLOR_MODES,
    buildRecolorResult,
    cloneImageData,
    formatFileSize,
    imageDataToPreviewUrl,
    isSupportedLogoColorFile,
    isValidHexColor,
    loadLogoImageData,
    recolorImageData,
} from './logoColorChangerImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const inputClass = 'w-full rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 font-mono text-[12px] text-[#3D3833] outline-none transition-colors focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

/** Glyph-over-label tile, the tray shape the other image tools use. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

// Recolour modes: keep the shading, flood one flat colour, swap one colour out.
const MODE_ICONS = { smart: IconWand, flat: IconBucketDroplet, replace: IconReplace };

/** A colour outside the presets means the picker tile is the live one. */
function isLightHex(hex) {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return true;
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
}

export default function LogoColorChangerImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const originalImageDataRef = useRef(null);
    const previewImageDataRef = useRef(null);

    const [file, setFile] = useState(null);
    const [mode, setMode] = useState('smart');
    const [newColor, setNewColor] = useState(COLOR_PRESETS[0]);
    const [fromColor, setFromColor] = useState('#000000');
    const [tolerance, setTolerance] = useState(48);
    const [format, setFormat] = useState('png');
    const [isDragging, setIsDragging] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewMeta, setPreviewMeta] = useState(null);
    const [sourceReady, setSourceReady] = useState(0);
    const [downloaded, setDownloaded] = useState(false);
    const [error, setError] = useState(null);

    const recolorOptions = useMemo(() => ({
        mode,
        newColor,
        fromColor,
        tolerance,
    }), [mode, newColor, fromColor, tolerance]);

    const applyLivePreview = useCallback(() => {
        const source = originalImageDataRef.current;
        if (!file || !source) return;

        if (!isValidHexColor(newColor)) return;
        if (mode === 'replace' && !isValidHexColor(fromColor)) return;

        const recolored = cloneImageData(source);
        const applied = recolorImageData(recolored, recolorOptions);
        if (!applied) return;

        previewImageDataRef.current = recolored;
        setPreviewUrl(imageDataToPreviewUrl(recolored));
        setError(null);
    }, [file, recolorOptions, mode, newColor, fromColor]);

    const addFile = useCallback((incoming) => {
        const picked = Array.from(incoming || [])[0];
        if (!picked) return;

        if (!isSupportedLogoColorFile(picked)) {
            const msg = t('logoColorChangerImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        if (picked.size > MAX_FILE_BYTES) {
            const msg = t('logoColorChangerImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        originalImageDataRef.current = null;
        previewImageDataRef.current = null;
        setFile(picked);
        setPreviewUrl(null);
        setPreviewMeta(null);
        setSourceReady(0);
        setDownloaded(false);
        setError(null);
    }, [t]);

    useEffect(() => {
        if (!file) {
            originalImageDataRef.current = null;
            previewImageDataRef.current = null;
            setPreviewUrl(null);
            setPreviewMeta(null);
            return undefined;
        }

        let cancelled = false;
        loadLogoImageData(file)
            .then(({ imageData, width, height }) => {
                if (cancelled) return;
                originalImageDataRef.current = imageData;
                previewImageDataRef.current = null;
                setPreviewMeta({ width, height, originalSize: file.size });
                setPreviewUrl(null);
                setSourceReady((v) => v + 1);
            })
            .catch(() => {
                if (cancelled) return;
                const msg = t('logoColorChangerImage.errors.failed');
                setError(msg);
                setPreviewUrl(null);
                setPreviewMeta(null);
            });

        return () => {
            cancelled = true;
        };
    }, [file, t]);

    useEffect(() => {
        applyLivePreview();
    }, [applyLivePreview, sourceReady]);

    // Ctrl/Cmd+V anywhere on the page drops a copied logo straight in.
    useImagePaste(addFile);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleDownload = async () => {
        const imageData = previewImageDataRef.current;
        if (!file || !imageData) return;

        recordToolUsage('image/logo-color-changer', { category: 'image' });

        try {
            const result = await buildRecolorResult(file, imageData, file.size, format);
            downloadBlob(result.blob, result.name);
            setDownloaded(true);
            notifications.show({
                title: t('common.success'),
                message: t('logoColorChangerImage.done'),
                color: 'green',
            });
        } catch {
            const msg = t('logoColorChangerImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        }
    };

    const handleReset = () => {
        originalImageDataRef.current = null;
        previewImageDataRef.current = null;
        setFile(null);
        setMode('smart');
        setNewColor(COLOR_PRESETS[0]);
        setFromColor('#000000');
        setTolerance(48);
        setPreviewUrl(null);
        setPreviewMeta(null);
        setSourceReady(0);
        setDownloaded(false);
        setError(null);
    };

    const hasPreview = Boolean(previewUrl && previewImageDataRef.current);

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: hasPreview,
        processing: false,
        hasResult: downloaded,
        hasError: !!error,
    });

    const isCustomColor = !COLOR_PRESETS.some(
        (color) => color.toLowerCase() === newColor.toLowerCase(),
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('logoColorChangerImage.done'),
    });

    const sizeNote = previewMeta && previewImageDataRef.current ? (
        <>
            <span className="font-semibold text-[#1E9E5E]">
                {toBnDigits(previewMeta.width)}×{toBnDigits(previewMeta.height)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({formatFileSize(previewMeta.originalSize)})
            </span>
        </>
    ) : null;

    return (
        <ToolPageLayout
            slug="image/logo-color-changer"
            subtitleKey="logoColorChangerImage.subtitle"
            status={status}
            statusText={sizeNote && !downloaded
                ? <span className="inline-flex flex-wrap items-center gap-1.5">{sizeNote}</span>
                : statusText}
            onReset={file || error ? handleReset : undefined}
            primaryAction={{
                label: t('logoColorChangerImage.download'),
                onClick: handleDownload,
                disabled: !hasPreview,
                icon: IconDownload,
            }}
            showTrust={false}
            // The preview column tracks the settings column, so the two sides end
            // level. The settings here are short, so the row carries a floor —
            // otherwise the before/after panel would be squeezed into ~350px.
            matchSidebarHeight
            matchSidebarMinHeight="min(620px, 72vh)"
            settings={(
                <div className="flex flex-col gap-3">
                    {/* The logo is picked in the preview panel, so the left column
                        carries settings only — this input is what the empty state
                        and the button below open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />
                    <ToolCard title={t('logoColorChangerImage.modeTitle')}>
                        <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only. The tooltip leads with the mode's name —
                                with no label on the button, the hint alone would
                                never say which mode is which — and aria-label
                                carries the name for screen readers. */}
                            {RECOLOR_MODES.map((id) => {
                                const Icon = MODE_ICONS[id] || IconWand;
                                const name = t(`logoColorChangerImage.modes.${id}`);
                                return (
                                    <Hint label={`${name} — ${t(`logoColorChangerImage.modeHints.${id}`)}`} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => setMode(id)}
                                            aria-label={name}
                                            aria-pressed={mode === id}
                                            className={tileBtnClass(mode === id)}
                                        >
                                            <Icon size={20} stroke={1.8} />
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                        <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t(`logoColorChangerImage.modeHints.${mode}`)}
                        </p>
                    </ToolCard>

                    <ToolCard title={t('logoColorChangerImage.newColorTitle')}>
                        {/* Presets and the free picker on one row — the last tile
                            is the picker, tinted with the colour it holds. */}
                        {/* Seven presets plus the picker on one line — 8 fixed
                            squares fit the settings column's width. */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {COLOR_PRESETS.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setNewColor(color)}
                                    aria-label={color}
                                    className={`h-8 w-8 flex-none rounded-lg border-2 transition-transform hover:scale-105 ${
                                        newColor.toLowerCase() === color.toLowerCase()
                                            ? 'border-amber-500 ring-2 ring-amber-400/40'
                                            : 'border-[#ECE8E4] dark:border-zinc-700'
                                    }`}
                                    style={{ backgroundColor: color }}
                                />
                            ))}
                            <Hint label={t('logoColorChangerImage.customColor')}>
                            <span
                                className={`relative flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border-2 transition-transform ${
                                    isCustomColor
                                        ? 'border-amber-500 ring-2 ring-amber-400/40'
                                        : 'border-[#ECE8E4] dark:border-zinc-700'
                                }`}
                                style={isCustomColor && isValidHexColor(newColor)
                                    ? { backgroundColor: newColor }
                                    : undefined}
                            >
                                <IconColorPicker
                                    size={16}
                                    stroke={2}
                                    className={isCustomColor && isValidHexColor(newColor) && !isLightHex(newColor)
                                        ? 'text-white'
                                        : 'text-[#6B635C] dark:text-zinc-300'}
                                />
                                <input
                                    type="color"
                                    value={isValidHexColor(newColor) ? newColor : '#000000'}
                                    aria-label={t('logoColorChangerImage.customColor')}
                                    onChange={(e) => setNewColor(e.target.value)}
                                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                            </span>
                            </Hint>
                        </div>
                    </ToolCard>

                    {mode === 'replace' ? (
                        <ToolCard title={t('logoColorChangerImage.replaceTitle')}>
                            <label className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('logoColorChangerImage.fromColor')}
                                </span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={isValidHexColor(fromColor) ? fromColor : '#000000'}
                                        onChange={(e) => setFromColor(e.target.value)}
                                        className="h-10 w-12 cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                    />
                                    <input
                                        type="text"
                                        value={fromColor}
                                        onChange={(e) => setFromColor(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </label>

                            <label htmlFor="logo-color-tolerance" className="mt-3 block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('logoColorChangerImage.toleranceLabel', { value: toBnDigits(tolerance) })}
                                </span>
                                <input
                                    id="logo-color-tolerance"
                                    type="range"
                                    min={8}
                                    max={120}
                                    value={tolerance}
                                    onChange={(e) => setTolerance(Number(e.target.value))}
                                    className="w-full accent-amber-500"
                                />
                            </label>
                        </ToolCard>
                    ) : null}

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); setDownloaded(false); }}
                        formats={['png', 'webp', 'jpg', 'avif']}
                        transparentOutput
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('logoColorChangerImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('logoColorChangerImage.previewEmpty')}
                    idleHint={t('logoColorChangerImage.previewEmptyHint')}
                    error={error}
                    canvasTone="plain"
                    fill
                    tall={false}
                    showPasteHint
                    showIdle={!file && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {file ? (
                        <LogoColorChangerPreview
                            file={file}
                            previewUrl={previewUrl}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
